import { DIRECTIONS, type CellId, type Direction } from './types.ts';

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** Unit vectors, y grows southwards (row 1 is the top, R-MAP-02). */
export const DIRECTION_VECTORS: Readonly<Record<Direction, Point>> = {
  N: { x: 0, y: -1 },
  NE: { x: 1, y: -1 },
  E: { x: 1, y: 0 },
  SE: { x: 1, y: 1 },
  S: { x: 0, y: 1 },
  SW: { x: -1, y: 1 },
  W: { x: -1, y: 0 },
  NW: { x: -1, y: -1 },
};

/** Returns the direction rotated by `steps` eighths of a turn (positive = clockwise). */
export function rotate(direction: Direction, steps: number): Direction {
  const index = DIRECTIONS.indexOf(direction);
  const next = (((index + steps) % 8) + 8) % 8;
  return DIRECTIONS[next] as Direction;
}

/** Number of 45° steps between two directions, 0..4 (R-MOVE-02). */
export function angleSteps(a: Direction, b: Direction): number {
  const d = (DIRECTIONS.indexOf(a) - DIRECTIONS.indexOf(b) + 8) % 8;
  return Math.min(d, 8 - d);
}

export function columnLabel(x: number): string {
  let label = '';
  let n = x + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    label = String.fromCharCode(65 + r) + label;
    n = Math.floor((n - 1) / 26);
  }
  return label;
}

export function toCellId(p: Point): CellId {
  return `${columnLabel(p.x)}${p.y + 1}`;
}

const CELL_RE = /^([A-Z]+)([1-9][0-9]*)$/;

export function parseCellId(id: CellId): Point | null {
  const m = CELL_RE.exec(id);
  if (!m) return null;
  const letters = m[1] as string;
  let x = 0;
  for (const ch of letters) x = x * 26 + (ch.charCodeAt(0) - 64);
  return { x: x - 1, y: Number(m[2]) - 1 };
}

export function mustParse(id: CellId): Point {
  const p = parseCellId(id);
  if (!p) throw new Error(`Invalid cell id: ${id}`);
  return p;
}

/**
 * If `to` lies on one of the 8 rays from `from`, returns the direction and distance.
 * Distance 0 (same cell) returns `{ direction: null, distance: 0 }`.
 */
export function rayBetween(from: Point, to: Point): { direction: Direction | null; distance: number } | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (dx === 0 && dy === 0) return { direction: null, distance: 0 };
  if (dx !== 0 && dy !== 0 && Math.abs(dx) !== Math.abs(dy)) return null;
  const sx = Math.sign(dx);
  const sy = Math.sign(dy);
  const direction = DIRECTIONS.find(
    (d) => DIRECTION_VECTORS[d].x === sx && DIRECTION_VECTORS[d].y === sy,
  ) as Direction;
  return { direction, distance: Math.max(Math.abs(dx), Math.abs(dy)) };
}

export function step(p: Point, d: Direction, n = 1): Point {
  const v = DIRECTION_VECTORS[d];
  return { x: p.x + v.x * n, y: p.y + v.y * n };
}

export function isDiagonal(d: Direction): boolean {
  const v = DIRECTION_VECTORS[d];
  return v.x !== 0 && v.y !== 0;
}

export function samePoint(a: Point, b: Point): boolean {
  return a.x === b.x && a.y === b.y;
}
